import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

// Types
export interface ParticipantProfile {
    age_range: string;
    occupation: string;
    education: string;
    internet_frequency: string;
    judicial_experience: string;
    device_preference: string;
    accessibility_needs: string[];
    language_preference: 'simple' | 'technical';
    tcle_accepted: boolean;
}

export type PersonaId = 'olivia' | 'john_smith' | 'dr_charles' | 'dra_emily';

export interface TaskAssignment {
    task_index: number;
    execution_id: string;
    interface_id: string;
    task_type: 'status_query' | 'decision_interpretation';
    process_phase: string;
    ui_spec: Record<string, unknown>;
}

export interface InteractionData {
    time_on_page_ms: number;
    scroll_events: ScrollEvent[];
    click_events: ClickEvent[];
    max_scroll_depth: number;
}

export interface ScrollEvent {
    timestamp: number;
    scroll_depth_percent: number;
    scroll_y: number;
}

export interface ClickEvent {
    timestamp: number;
    element_id: string;
    element_type: string;
    element_text?: string;
    x: number;
    y: number;
}

export interface FeedbackData {
    task_completion: 'complete' | 'partial' | 'failed';
    clarity_score: number;        // 1-5
    ease_of_use_score: number;    // 1-5
    visual_score: number;         // 1-5
    suggestions: string;
}

export interface CompletedTask {
    task_index: number;
    execution_id: string;
    interaction_data: InteractionData;
    feedback: FeedbackData;
    completed_at: string;
}

export interface DeviceInfo {
    user_agent: string;
    screen_width: number;
    screen_height: number;
    device_type: 'mobile' | 'tablet' | 'desktop';
    browser: string;
}

export interface ParticipantSession {
    session_id: string;
    participant_id: string;
    profile: ParticipantProfile;
    determined_persona: PersonaId;
    assigned_tasks: TaskAssignment[];
    current_task_index: number;
    completed_tasks: CompletedTask[];
    started_at: string;
    device_info: DeviceInfo;
}

// State
interface ParticipantState {
    session: ParticipantSession | null;
    isLoading: boolean;
    error: string | null;
    currentInteraction: InteractionData | null;
}

// Actions
type ParticipantAction =
    | { type: 'START_SESSION'; payload: ParticipantSession }
    | { type: 'UPDATE_PROFILE'; payload: Partial<ParticipantProfile> }
    | { type: 'SET_TASKS'; payload: TaskAssignment[] }
    | { type: 'COMPLETE_TASK'; payload: CompletedTask }
    | { type: 'NEXT_TASK' }
    | { type: 'UPDATE_INTERACTION'; payload: Partial<InteractionData> }
    | { type: 'SET_ERROR'; payload: string }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'END_SESSION' };

// Reducer
function participantReducer(state: ParticipantState, action: ParticipantAction): ParticipantState {
    switch (action.type) {
        case 'START_SESSION':
            return {
                ...state,
                session: action.payload,
                isLoading: false,
                error: null
            };

        case 'UPDATE_PROFILE':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    profile: { ...state.session.profile, ...action.payload }
                }
            };

        case 'SET_TASKS':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    assigned_tasks: action.payload
                }
            };

        case 'COMPLETE_TASK':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    completed_tasks: [...state.session.completed_tasks, action.payload]
                }
            };

        case 'NEXT_TASK':
            if (!state.session) return state;
            return {
                ...state,
                session: {
                    ...state.session,
                    current_task_index: state.session.current_task_index + 1
                },
                currentInteraction: null
            };

        case 'UPDATE_INTERACTION':
            return {
                ...state,
                currentInteraction: {
                    time_on_page_ms: state.currentInteraction?.time_on_page_ms ?? 0,
                    scroll_events: state.currentInteraction?.scroll_events ?? [],
                    click_events: state.currentInteraction?.click_events ?? [],
                    max_scroll_depth: state.currentInteraction?.max_scroll_depth ?? 0,
                    ...action.payload
                }
            };

        case 'SET_ERROR':
            return { ...state, error: action.payload, isLoading: false };

        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };

        case 'END_SESSION':
            return { ...state, session: null, currentInteraction: null };

        default:
            return state;
    }
}

// Determine persona based on profile
export function determinePersona(profile: ParticipantProfile): PersonaId {
    const isLawyer = profile.occupation === 'Advogado';
    const hasLowVision = profile.accessibility_needs.includes('Baixa visão');
    const hasPhotophobia = profile.accessibility_needs.includes('Fotofobia');

    if (isLawyer) {
        // Dra. Emily: Advogada com fotofobia (pode ter baixa visão também)
        // Combinação: Zoom 200% + Dark Mode
        if (hasPhotophobia) return 'dra_emily';
        return 'dr_charles';
    } else {
        // João: Cidadão com baixa visão
        // Combinação: Zoom 200% + High Contrast
        if (hasLowVision) return 'john_smith';
        return 'olivia';
    }
}

// Get device info
function getDeviceInfo(): DeviceInfo {
    const ua = navigator.userAgent;
    const width = window.screen.width;
    const height = window.screen.height;

    let deviceType: 'mobile' | 'tablet' | 'desktop' = 'desktop';
    if (width < 576) {
        deviceType = 'mobile';
    } else if (width < 992) {
        deviceType = 'tablet';
    }

    let browser = 'unknown';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    return {
        user_agent: ua,
        screen_width: width,
        screen_height: height,
        device_type: deviceType,
        browser
    };
}

// Context
interface ParticipantContextType {
    state: ParticipantState;
    dispatch: React.Dispatch<ParticipantAction>;

    // Helper functions
    startSession: (profile: ParticipantProfile) => Promise<void>;
    submitFeedback: (feedback: FeedbackData) => Promise<void>;
    getCurrentTask: () => TaskAssignment | null;
    isLastTask: () => boolean;
    endSession: () => void;
}

const ParticipantContext = createContext<ParticipantContextType | null>(null);

const STORAGE_KEY = 'lumen_participant_session';
const BFF_URL = import.meta.env.VITE_BFF_URL || 'https://bff-service.lumenframework.com';

// Provider
export function ParticipantProvider({ children }: { children: React.ReactNode }) {
    const [state, dispatch] = useReducer(participantReducer, {
        session: null,
        isLoading: false,
        error: null,
        currentInteraction: null
    });

    // Restore session from localStorage
    useEffect(() => {
        const savedSession = localStorage.getItem(STORAGE_KEY);
        if (savedSession) {
            try {
                const session = JSON.parse(savedSession);
                dispatch({ type: 'START_SESSION', payload: session });
            } catch {
                localStorage.removeItem(STORAGE_KEY);
            }
        }
    }, []);

    // Save session to localStorage
    useEffect(() => {
        if (state.session) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state.session));
        }
    }, [state.session]);

    const startSession = useCallback(async (profile: ParticipantProfile) => {
        dispatch({ type: 'SET_LOADING', payload: true });

        const persona = determinePersona(profile);
        const deviceInfo = getDeviceInfo();

        // Default mock tasks for offline/demo mode with complete UI specs
        const mockTasks: TaskAssignment[] = [
            {
                task_index: 0,
                execution_id: `mock_exec_${Date.now()}_0`,
                interface_id: `mock_ui_0`,
                task_type: 'status_query',
                process_phase: 'em_analise',
                ui_spec: {
                    components: [
                        {
                            type: 'br-header',
                            props: { title: 'Consulta Processual', subtitle: 'Tribunal Superior do Trabalho' }
                        },
                        {
                            type: 'br-card',
                            props: { title: 'Status do Processo' },
                            children: [
                                { type: 'br-text', props: { content: 'Processo: RR-1234-56.2024.5.10.0001' } },
                                { type: 'br-text', props: { content: 'Fase: Em Análise pelo Relator' } },
                                { type: 'br-message', props: { state: 'info', content: 'Seu processo está sendo analisado. Aguarde novas movimentações.' } }
                            ]
                        },
                        {
                            type: 'br-card',
                            props: { title: 'Últimas Movimentações' },
                            children: [
                                {
                                    type: 'br-list', props: {
                                        items: [
                                            { title: '15/12/2024', description: 'Conclusos ao relator para análise' },
                                            { title: '10/12/2024', description: 'Juntada de petição da parte reclamante' },
                                            { title: '05/12/2024', description: 'Recebimento dos autos do TRT' }
                                        ]
                                    }
                                }
                            ]
                        }
                    ]
                }
            },
            {
                task_index: 1,
                execution_id: `mock_exec_${Date.now()}_1`,
                interface_id: `mock_ui_1`,
                task_type: 'decision_interpretation',
                process_phase: 'decisao_publicada',
                ui_spec: {
                    components: [
                        {
                            type: 'br-header',
                            props: { title: 'Decisão do Processo', subtitle: 'Tribunal Superior do Trabalho' }
                        },
                        {
                            type: 'br-message',
                            props: { state: 'success', content: 'Decisão favorável! O recurso foi provido parcialmente.' }
                        },
                        {
                            type: 'br-card',
                            props: { title: 'Resumo da Decisão' },
                            children: [
                                { type: 'br-text', props: { content: 'O Tribunal reconheceu o direito ao pagamento de horas extras, conforme solicitado na petição inicial.' } },
                                { type: 'br-text', props: { content: 'Valor estimado: R$ 15.432,00' } }
                            ]
                        },
                        {
                            type: 'br-card',
                            props: { title: 'Próximos Passos' },
                            children: [
                                {
                                    type: 'br-list', props: {
                                        items: [
                                            { title: 'Prazo para recurso', description: '8 dias úteis (até 28/12/2024)' },
                                            { title: 'Início da execução', description: 'Após trânsito em julgado' }
                                        ]
                                    }
                                }
                            ]
                        }
                    ]
                }
            }
        ];

        let sessionData = {
            session_id: `session_${Date.now()}`,
            participant_id: `participant_${Date.now()}`,
            tasks: mockTasks
        };

        // Try to register with BFF, but don't fail if it doesn't work
        try {
            const response = await fetch(`${BFF_URL}/api/v1/participant/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    profile,
                    persona,
                    device_info: deviceInfo
                })
            });

            if (response.ok) {
                const data = await response.json();
                sessionData = {
                    session_id: data.session_id || sessionData.session_id,
                    participant_id: data.participant_id || sessionData.participant_id,
                    tasks: data.tasks?.length > 0 ? data.tasks : mockTasks
                };
            }
            // If response not ok, just use mock data (no throw)
        } catch (_error) {
            // Network error - use mock data, continue silently
            console.warn('BFF unavailable, using offline mode');
        }

        const session: ParticipantSession = {
            session_id: sessionData.session_id,
            participant_id: sessionData.participant_id,
            profile,
            determined_persona: persona,
            assigned_tasks: sessionData.tasks,
            current_task_index: 0,
            completed_tasks: [],
            started_at: new Date().toISOString(),
            device_info: deviceInfo
        };

        // CRITICAL: Save to localStorage BEFORE dispatch to avoid race condition
        localStorage.setItem(STORAGE_KEY, JSON.stringify(session));

        dispatch({ type: 'START_SESSION', payload: session });
    }, []);

    const getCurrentTask = useCallback((): TaskAssignment | null => {
        if (!state.session) return null;
        return state.session.assigned_tasks[state.session.current_task_index] || null;
    }, [state.session]);

    const submitFeedback = useCallback(async (feedback: FeedbackData) => {
        if (!state.session || !state.currentInteraction) return;

        const currentTask = getCurrentTask();
        if (!currentTask) return;

        const completedTask: CompletedTask = {
            task_index: state.session.current_task_index,
            execution_id: currentTask.execution_id,
            interaction_data: state.currentInteraction,
            feedback,
            completed_at: new Date().toISOString()
        };

        try {
            await fetch(`${BFF_URL}/api/v1/participant/${state.session.participant_id}/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...completedTask,
                    session_id: state.session.session_id
                })
            });

            dispatch({ type: 'COMPLETE_TASK', payload: completedTask });
            dispatch({ type: 'NEXT_TASK' });
        } catch (error) {
            dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
        }
    }, [state.session, state.currentInteraction, getCurrentTask]);

    const isLastTask = useCallback((): boolean => {
        if (!state.session) return false;
        return state.session.current_task_index >= state.session.assigned_tasks.length - 1;
    }, [state.session]);

    const endSession = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        dispatch({ type: 'END_SESSION' });
    }, []);

    return (
        <ParticipantContext.Provider value={{
            state,
            dispatch,
            startSession,
            submitFeedback,
            getCurrentTask,
            isLastTask,
            endSession
        }}>
            {children}
        </ParticipantContext.Provider>
    );
}

// Hook
export function useParticipant() {
    const context = useContext(ParticipantContext);
    if (!context) {
        throw new Error('useParticipant must be used within ParticipantProvider');
    }
    return context;
}
